import { memo } from 'react';
import classnames from 'classnames';

import Icon from 'components/atoms/Icon';

import infoIcon from 'images/icons/information-white.png';
import infoHoverIcon from 'images/icons/information-gray.png';
import positionIcon from 'images/icons/position-white.png';
import positionHoverIcon from 'images/icons/position-gray.png';
import highlightsOpenIcon from 'images/icons/highlights-open-white.png';
import highlightsOpenHoverIcon from 'images/icons/highlights-open-gray.png';
import highlightsCloseIcon from 'images/icons/highlights-close-white.png';
import highlightsCloseHoverIcon from 'images/icons/highlights-close-gray.png';
import tourPlayIcon from 'images/icons/tour-play-white.png';
import tourPlayHoverIcon from 'images/icons/tour-play-gray.png';
import tourPauseIcon from 'images/icons/tour-pause-white.png';
import tourPauseHoverIcon from 'images/icons/tour-pause-gray.png';
import viewIcon from 'images/icons/view-white.png';
import viewHoverIcon from 'images/icons/view-gray.png';
import upstairsIcon from 'images/icons/upstairs-white.png';
import upstairsHoverIcon from 'images/icons/upstairs-gray.png';
import downstairsIcon from 'images/icons/downstairs-white.png';
import downstairsHoverIcon from 'images/icons/downstairs-gray.png';
import musicIcon from 'images/icons/music-white.png';
import musicHoverIcon from 'images/icons/music-gray.png';
import muteIconIcon from 'images/icons/music-mute-white.png';
import muteHoverIcon from 'images/icons/music-mute-gray.png';
import shareIcon from 'images/icons/share-white.png';
import shareHoverIcon from 'images/icons/share-gray.png';
import cameraIcon from 'images/icons/camera-white.png';
import locationIcon from 'images/icons/location-white.png';

import styles from './index.css';

export type View = 'walking' | 'dollhouse' | 'floorPlan';

export type Tool =
    | 'info'
    | 'position'
    | 'highlights'
    | 'tour'
    | 'view'
    | 'floors'
    | 'music'
    | 'share'
    | 'screenshot'
    | 'getLocation';

interface ToolData {
    enable: boolean;
    value: boolean | View | number | string;
    data?: any;
}

export interface ToolbarProp extends Partial<Record<Tool, ToolData | boolean>> {
    className?: string;
    onClick?: () => void;
    onStateUpdate?: (key: Tool, value?: View | number) => void;
}

const defaults: Record<Tool, ToolData> = {
    info: { enable: true, value: false },
    position: { enable: true, value: false },
    highlights: { enable: true, value: false },
    tour: { enable: true, value: false },
    view: { enable: true, value: 'walking' },
    floors: {
        enable: true,
        value: 1,
        data: {
            options: [],
        },
    },
    music: { enable: true, value: false },
    share: { enable: true, value: false },
    screenshot: { enable: false, value: false },
    getLocation: { enable: false, value: false },
};

const Toolbar: React.FC<ToolbarProp> = ({
    className,
    info,
    position,
    highlights,
    tour,
    view,
    floors,
    music,
    share,
    screenshot,
    getLocation,
    onClick = () => {},
    onStateUpdate = () => {},
}) => {
    return (
        <div className={classnames(styles.toolbar, className)}>
            {info && (
                <Icon
                    className={styles.item}
                    label="info"
                    icon={{
                        default: infoIcon,
                        hover: infoHoverIcon,
                    }}
                    onClick={() => {
                        onClick();
                        onStateUpdate('info');
                    }}
                />
            )}
            {position && (
                <Icon
                    className={styles.item}
                    label="position"
                    icon={{
                        default: positionIcon,
                        hover: positionHoverIcon,
                    }}
                    onClick={() => {
                        onClick();
                        onStateUpdate('position');
                    }}
                />
            )}
            {highlights && typeof highlights === 'object' && highlights.enable && (
                <Icon
                    className={styles.item}
                    label="highlights"
                    icon={{
                        default: highlightsOpenIcon,
                        hover: highlightsOpenHoverIcon,
                    }}
                    activeIcon={{
                        default: highlightsCloseIcon,
                        hover: highlightsCloseHoverIcon,
                    }}
                    active={
                        typeof highlights === 'object'
                            ? (highlights.value as boolean)
                            : (defaults.highlights.value as boolean)
                    }
                    onClick={() => {
                        onClick();
                        onStateUpdate('highlights');
                    }}
                />
            )}
            {tour && typeof tour === 'object' && tour.enable && (
                <Icon
                    className={styles.item}
                    label="tour"
                    icon={{
                        default: tourPlayIcon,
                        hover: tourPlayHoverIcon,
                    }}
                    activeIcon={{
                        default: tourPauseIcon,
                        hover: tourPauseHoverIcon,
                    }}
                    active={
                        typeof tour === 'object'
                            ? (tour.value as boolean)
                            : (defaults.tour.value as boolean)
                    }
                    onClick={() => {
                        onClick();
                        onStateUpdate('tour');
                    }}
                />
            )}
            {view && (
                <Icon
                    className={styles.item}
                    label="view"
                    icon={{
                        default: viewIcon,
                        hover: viewHoverIcon,
                    }}
                    options={[
                        {
                            label: 'Walk',
                            value: 'walking',
                        },
                        {
                            label: 'Dollhouse',
                            value: 'dollhouse',
                        },
                        {
                            label: 'Floor plan',
                            value: 'floorPlan',
                        },
                    ]}
                    onClick={onClick}
                    onClickOption={({ value }) => {
                        onStateUpdate('view', value as View);
                    }}
                />
            )}
            {floors &&
                typeof floors === 'object' &&
                floors.enable &&
                floors.data?.options?.length > 1 && (
                    <Icon
                        className={styles.item}
                        label="floors"
                        icon={{
                            default: upstairsIcon,
                            hover: upstairsHoverIcon,
                        }}
                        activeIcon={{
                            default: downstairsIcon,
                            hover: downstairsHoverIcon,
                        }}
                        options={floors.data?.options}
                        optionsReverse
                        active={typeof floors.value === 'number' && floors.value > 1}
                        onClick={onClick}
                        onClickOption={({ value }) => {
                            onStateUpdate('floors', value as number);
                        }}
                    />
                )}
            {music && typeof music === 'object' && music.enable && (
                <Icon
                    className={styles.item}
                    label="music"
                    icon={{
                        default: muteIconIcon,
                        hover: muteHoverIcon,
                    }}
                    activeIcon={{
                        default: musicIcon,
                        hover: musicHoverIcon,
                    }}
                    active={
                        typeof music === 'object'
                            ? (music.value as boolean)
                            : (defaults.music.value as boolean)
                    }
                    onClick={() => {
                        onClick();
                        onStateUpdate('music');
                    }}
                />
            )}
            {share && (
                <Icon
                    className={styles.item}
                    label="share"
                    icon={{
                        default: shareIcon,
                        hover: shareHoverIcon,
                    }}
                    onClick={() => {
                        onClick();
                        onStateUpdate('share');
                    }}
                />
            )}
            {screenshot && (
                <Icon
                    className={styles.item}
                    label="screenshot"
                    icon={{
                        default: cameraIcon,
                        hover: cameraIcon,
                    }}
                    onClick={() => {
                        onClick();
                        onStateUpdate('screenshot');
                    }}
                />
            )}
            {getLocation && (
                <Icon
                    className={styles.item}
                    label="get location"
                    icon={{
                        default: locationIcon,
                        hover: locationIcon,
                    }}
                    onClick={() => {
                        onClick();
                        onStateUpdate('getLocation');
                    }}
                />
            )}
        </div>
    );
};

export default memo(Toolbar);
